/**
 * Lecteur de messages vocaux façon WhatsApp — alpha, item 3.
 *
 * Remplace l'ancienne bulle `▶ + barre statique` par un vrai lecteur :
 *
 *   - **Waveform** : le blob audio est décodé (`decodeAudioData`) une
 *     seule fois par message, réduit en N barres normalisées
 *     (`computePeaks`) et mis en cache mémoire. Fallback : barres
 *     uniformes si le décodage échoue (codec exotique, WebAudio
 *     indisponible).
 *   - **Scrub** : cliquer/glisser sur la waveform → seek proportionnel.
 *   - **Vitesse** : bouton cycle 1× → 1.5× → 2× (persisté module-level,
 *     comme WhatsApp qui garde la vitesse choisie).
 *   - **Un seul vocal à la fois** : un unique `HTMLAudioElement`
 *     module-level ; lancer un vocal met en pause le précédent.
 *   - **Durée fiable** : les blobs `MediaRecorder` ont souvent
 *     `duration = Infinity` (bug Chromium connu) — la durée vient du
 *     décodage WebAudio, pas de l'élément `<audio>`.
 *
 * Le re-render passe par `bumpUi()` (pattern messages/View.ts).
 */
import { html, type TemplateResult } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './View.module.css';
import { t } from '../../i18n/index.js';
import { bumpUi } from '../../stores/ui.js';

/** Nombre de barres de la waveform (compromis lisibilité/largeur). */
export const WAVE_BUCKETS = 28;

/** Vitesses de lecture, cycle dans cet ordre. */
const RATES = [1, 1.5, 2] as const;

// ── Helpers purs (testés unitairement) ────────────────────────────────

/**
 * Réduit un canal PCM en `buckets` pics normalisés 0..1 (RMS par
 * tranche, normalisé par le pic max). Retourne des barres uniformes
 * moyennes si le signal est vide/silencieux (pas de division par 0).
 */
export function computePeaks(channelData: Float32Array, buckets: number): number[] {
  if (buckets <= 0) return [];
  if (channelData.length === 0) return Array.from({ length: buckets }, () => 0.4);
  const bucketSize = Math.max(1, Math.floor(channelData.length / buckets));
  const peaks: number[] = [];
  for (let b = 0; b < buckets; b += 1) {
    const start = b * bucketSize;
    const end = Math.min(start + bucketSize, channelData.length);
    let sum = 0;
    for (let i = start; i < end; i += 1) {
      const v = channelData[i]!;
      sum += v * v;
    }
    const count = Math.max(1, end - start);
    peaks.push(Math.sqrt(sum / count));
  }
  const max = Math.max(...peaks);
  if (max <= 0) return peaks.map(() => 0.4);
  // Plancher 0.12 : une barre reste visible même sur un passage silencieux.
  return peaks.map((p) => Math.max(0.12, p / max));
}

/** Formate des secondes en `m:ss` (arrondi à la seconde inférieure). */
export function fmtDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString()}:${s.toString().padStart(2, '0')}`;
}

// ── Cache waveform (par message id) ───────────────────────────────────

interface WaveData {
  peaks: number[];
  /** Durée en secondes (source : décodage WebAudio). */
  duration: number;
}

type WaveEntry = WaveData | 'pending' | 'error';

const waveCache = new Map<string, WaveEntry>();

/** Barres uniformes de secours (décodage impossible). */
function fallbackPeaks(): number[] {
  return Array.from({ length: WAVE_BUCKETS }, (_, i) => 0.25 + 0.5 * Math.abs(Math.sin(i * 0.9)));
}

async function decodeWave(msgId: string, src: string): Promise<void> {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) throw new Error('no AudioContext');
    const res = await fetch(src);
    const buf = await res.arrayBuffer();
    const ctx = new Ctor();
    try {
      const audio = await ctx.decodeAudioData(buf);
      waveCache.set(msgId, {
        peaks: computePeaks(audio.getChannelData(0), WAVE_BUCKETS),
        duration: audio.duration,
      });
    } finally {
      void ctx.close();
    }
  } catch {
    waveCache.set(msgId, 'error');
  }
  bumpUi();
}

/**
 * Waveform d'un message : lance le décodage au premier appel (résultat
 * via `bumpUi`), sert le cache ensuite. `null` = pas encore prête.
 */
function waveFor(msgId: string, src: string | null): WaveData | null {
  if (!src) return null;
  const entry = waveCache.get(msgId);
  if (entry === undefined) {
    waveCache.set(msgId, 'pending');
    void decodeWave(msgId, src);
    return null;
  }
  if (entry === 'pending') return null;
  if (entry === 'error') return { peaks: fallbackPeaks(), duration: 0 };
  return entry;
}

// ── Lecture (un seul vocal à la fois) ─────────────────────────────────

interface PlayerState {
  /** Message en cours de lecture/pause (null = lecteur fermé). */
  id: string | null;
  playing: boolean;
  /** Position courante en secondes (maj via timeupdate). */
  time: number;
  /** Index dans RATES — persiste d'un vocal à l'autre (comme WhatsApp). */
  rateIdx: number;
}

const player: PlayerState = { id: null, playing: false, time: 0, rateIdx: 0 };

let audioEl: HTMLAudioElement | null = null;

function ensureAudio(): HTMLAudioElement {
  if (audioEl) return audioEl;
  audioEl = new Audio();
  audioEl.addEventListener('timeupdate', () => {
    player.time = audioEl?.currentTime ?? 0;
    bumpUi();
  });
  audioEl.addEventListener('ended', () => {
    player.playing = false;
    player.time = 0;
    bumpUi();
  });
  audioEl.addEventListener('pause', () => {
    player.playing = false;
    bumpUi();
  });
  audioEl.addEventListener('play', () => {
    player.playing = true;
    bumpUi();
  });
  return audioEl;
}

function togglePlay(msgId: string, src: string): void {
  const audio = ensureAudio();
  if (player.id === msgId) {
    if (audio.paused) void audio.play();
    else audio.pause();
    return;
  }
  // Nouveau vocal : stoppe l'éventuel précédent, charge, joue.
  audio.pause();
  player.id = msgId;
  player.time = 0;
  audio.src = src;
  audio.playbackRate = RATES[player.rateIdx]!;
  void audio.play();
  bumpUi();
}

function cycleRate(): void {
  player.rateIdx = (player.rateIdx + 1) % RATES.length;
  if (audioEl) audioEl.playbackRate = RATES[player.rateIdx]!;
  bumpUi();
}

function seekTo(msgId: string, src: string, fraction: number, duration: number): void {
  const audio = ensureAudio();
  if (player.id !== msgId) {
    // Vocal pas encore chargé : on le sélectionne en pause à la position.
    audio.pause();
    player.id = msgId;
    audio.src = src;
    audio.playbackRate = RATES[player.rateIdx]!;
  }
  const target = Math.max(0, Math.min(0.999, fraction)) * duration;
  audio.currentTime = target;
  player.time = target;
  bumpUi();
}

/** @internal pour tests — reset l'état du lecteur + cache. */
export function _resetVoicePlayerForTests(): void {
  audioEl?.pause();
  audioEl = null;
  player.id = null;
  player.playing = false;
  player.time = 0;
  player.rateIdx = 0;
  waveCache.clear();
}

// ── Template bulle vocale ─────────────────────────────────────────────

export function voiceBubbleTemplate(
  msgId: string,
  audioSrc: string | null,
  isMine: boolean,
): TemplateResult {
  const wave = waveFor(msgId, audioSrc);
  const isCurrent = player.id === msgId;
  const playing = isCurrent && player.playing;
  const duration = wave?.duration ?? 0;
  const time = isCurrent ? player.time : 0;
  const progress = duration > 0 ? Math.min(1, time / duration) : 0;
  const peaks = wave?.peaks ?? fallbackPeaks();
  const rateLabel = `${RATES[player.rateIdx]!.toString()}×`;

  const onScrub = (e: PointerEvent): void => {
    if (!audioSrc || duration <= 0) return;
    const el = e.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    seekTo(msgId, audioSrc, (e.clientX - rect.left) / rect.width, duration);
  };

  return html`<div
    class=${classMap({ [styles.voiceBubble!]: true, [styles.voiceBubbleMine!]: isMine })}
    data-testid="voice-bubble"
  >
    <button
      type="button"
      class=${styles.voicePlay}
      aria-label=${playing ? t('voice_pause') : t('voice_message')}
      data-testid="voice-play"
      ?disabled=${!audioSrc}
      @click=${(): void => {
        if (audioSrc) togglePlay(msgId, audioSrc);
      }}
    >
      ${playing ? '❚❚' : '▶'}
    </button>
    <div class=${styles.voiceMain}>
      <div
        class=${styles.voiceWave}
        data-testid="voice-wave"
        @pointerdown=${onScrub}
        role="slider"
        aria-label=${t('voice_seek')}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow=${Math.round(progress * 100).toString()}
      >
        ${peaks.map((p, i) => {
          const played = duration > 0 && i / peaks.length <= progress && isCurrent;
          return html`<span
            class=${classMap({ [styles.voiceWaveBar!]: true, [styles.voiceWaveBarOn!]: played })}
            style=${`height:${Math.round(p * 100).toString()}%`}
          ></span>`;
        })}
      </div>
      <span class=${styles.voiceTime} data-testid="voice-time"
        >${isCurrent && time > 0 ? fmtDuration(time) : fmtDuration(duration)}</span
      >
    </div>
    ${isCurrent
      ? html`<button
          type="button"
          class=${styles.voiceRate}
          data-testid="voice-rate"
          aria-label=${t('voice_rate')}
          @click=${cycleRate}
        >
          ${rateLabel}
        </button>`
      : null}
  </div>`;
}
